// src/components/Store/StorePage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../public/PublicLayout';
import SEO from '../common/SEO';
import { seoConfig } from '../../config/seoConfig';

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  light: '#f8f9fa'
};

const StorePage = ({ user, onLogout }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchProducts();
    fetchCategories();

    // Load cart from localStorage
    const savedCart = localStorage.getItem('arcaCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/store/products?status=active`);
      const data = await response.json();

      if (data.success) {
        setProducts(data.data || []);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/store/categories`);
      const data = await response.json();

      if (data.success) {
        setCategories([{ id: 'all', name: 'All Products' }, ...(data.data || [])]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      const categoryMatch = selectedCategory === 'all' || product.category_id === selectedCategory;
      const searchMatch = searchTerm === '' ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase()));
      return categoryMatch && searchMatch && product.status === 'active';
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price_low':
          return (a.price || 0) - (b.price || 0);
        case 'price_high':
          return (b.price || 0) - (a.price || 0);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    let newCart;

    if (existingItem) {
      newCart = cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      toast.success(`Increased quantity of ${product.name} in cart`);
    } else {
      newCart = [...cart, { ...product, quantity: 1 }];
      toast.success(`${product.name} added to cart`);
    }

    setCart(newCart);
    localStorage.setItem('arcaCart', JSON.stringify(newCart));
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <PublicLayout>
        <SEO
          title={seoConfig.store.title}
          description={seoConfig.store.description}
          keywords={seoConfig.store.keywords}
          url={seoConfig.store.url}
        />
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading ARCA Store...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.store.title}
        description={seoConfig.store.description}
        keywords={seoConfig.store.keywords}
        url={seoConfig.store.url}
      />
      <div className="py-4" style={{ backgroundColor: ARCA_COLORS.light, minHeight: '100vh' }}>
        <div className="container">
          {/* Page Header - Reduced Size */}
          <div className="row justify-content-center text-center mb-3">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                style={{ width: '50px', height: '50px', backgroundColor: ARCA_COLORS.primary }}>
                <i className="bi bi-shop fs-4 text-white"></i>
              </div>
              <h1 className="h2 fw-bold mb-2" style={{ color: ARCA_COLORS.secondary }}>ARCA Store</h1>
              <p className="text-muted small mb-0">Shop for respiratory therapy equipment, textbooks, and professional resources.</p>
            </div>
          </div>

          {/* COMING SOON Banner */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-10">
              <div className="card border-0 shadow-sm text-center py-5"
                style={{ background: `linear-gradient(135deg, ${ARCA_COLORS.secondary} 0%, ${ARCA_COLORS.primary} 100%)` }}>
                <div className="card-body">
                  <i className="bi bi-clock-history text-white mb-3" style={{ fontSize: '4rem' }}></i>
                  <h2 className="display-5 fw-bold text-white mb-3">COMING SOON</h2>
                  <p className="lead text-white mb-0">
                    Our online store is under construction. Check back soon for respiratory care equipment, textbooks, and professional resources.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Cart Badge */}
          <div className="mb-3 text-end">
            <Link
              to="/cart"
              className="btn position-relative"
              style={{ backgroundColor: ARCA_COLORS.primary, color: 'white', borderColor: ARCA_COLORS.primary }}
              title="Shopping Cart"
            >
              <i className="bi bi-cart3 me-2" style={{ fontSize: '1.25rem' }}></i>
              My Cart
              {getCartItemCount() > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  {getCartItemCount()}
                </span>
              )}
            </Link>
          </div>

          {/* Filter and Search Bar */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-body p-4">
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    {categories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`btn ${selectedCategory === category.id
                          ? 'text-white'
                          : 'btn-outline-secondary'
                          }`}
                        style={selectedCategory === category.id ? {
                          backgroundColor: ARCA_COLORS.primary,
                          borderColor: ARCA_COLORS.primary,
                          fontWeight: '500'
                        } : { fontWeight: '500' }}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                    <input
                      type="text"
                      placeholder="Search products by name, brand, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="form-select"
                  >
                    <option value="name">Sort by Name (A-Z)</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Products Count */}
          <div className="mb-2">
            <p className="text-muted">
              <strong>{filteredProducts.length}</strong> product{filteredProducts.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Products Grid - 6 products per row (col-lg-2) */}
          <div className="row g-3">
            {filteredProducts.length === 0 ? (
              <div className="col-12">
                <div className="alert alert-info text-center py-5">
                  <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                  <h4>No Products Found</h4>
                  <p className="mb-0">Try adjusting your search or filter criteria</p>
                </div>
              </div>
            ) : (
              filteredProducts.map(product => (
                <div key={product.id} className="col-sm-6 col-md-4 col-lg-2">
                  <div className="card shadow-sm border-0" style={{
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '';
                    }}>
                    {/* Full-size Product Image */}
                    <div className="position-relative" style={{
                      width: '100%',
                      paddingBottom: '100%',  // Changed from 75% to 100% for full square
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {product.main_image ? (
                        <img
                          src={product.main_image.startsWith('http') ? product.main_image : `${BACKEND_URL}${product.main_image}`}
                          alt={product.name}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate(`/store/product/${product.id}`)}
                          onError={(e) => {
                            e.target.src = '/api/placeholder/400/400';
                          }}
                        />
                      ) : (
                        <div className="bg-light d-flex align-items-center justify-content-center" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}>
                          <i className="bi bi-image text-muted" style={{ fontSize: '3rem' }}></i>
                        </div>
                      )}
                      {product.stock_quantity === 0 && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                          <span className="badge bg-dark" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            Out of Stock
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Price Section Only */}
                    <div className="card-body p-2 d-flex flex-column" style={{ flex: '0 0 auto' }}>
                      {/* Product Name */}
                      <h6 className="fw-bold mb-2" style={{
                        color: ARCA_COLORS.secondary,
                        fontSize: '0.85rem',
                        minHeight: '2.5rem',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {product.name}
                      </h6>

                      <div className="mb-2">
                        {product.compare_price && product.compare_price > product.price && (
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-decoration-line-through text-muted" style={{ fontSize: '0.7rem' }}>
                              {formatCurrency(product.compare_price)}
                            </span>
                            <span className="badge" style={{ backgroundColor: ARCA_COLORS.danger, fontSize: '0.6rem' }}>
                              Save {formatCurrency(product.compare_price - product.price)}
                            </span>
                          </div>
                        )}
                        <div className="d-flex align-items-baseline gap-1 mb-1">
                          <span className="h6 fw-bold mb-0" style={{ color: ARCA_COLORS.primary, fontSize: '0.9rem' }}>
                            {formatCurrency(product.price)}
                          </span>
                        </div>
                        {product.member_price && product.member_price < product.price && (
                          <div className="mb-1">
                            <span className="badge" style={{ backgroundColor: ARCA_COLORS.success, fontSize: '0.65rem' }}>
                              Member: {formatCurrency(product.member_price)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="d-grid gap-1 mt-auto">
                        <button
                          onClick={() => {
                            // Add to cart and redirect to checkout for immediate purchase
                            const existingItem = cart.find(item => item.id === product.id);
                            let newCart;
                            if (existingItem) {
                              newCart = cart.map(item =>
                                item.id === product.id
                                  ? { ...item, quantity: item.quantity + 1 }
                                  : item
                              );
                            } else {
                              newCart = [...cart, { ...product, quantity: 1 }];
                            }
                            setCart(newCart);
                            localStorage.setItem('arcaCart', JSON.stringify(newCart));
                            toast.success('Proceeding to checkout...');
                            navigate('/checkout');
                          }}
                          className="btn btn-sm text-white fw-bold"
                          style={{
                            backgroundColor: ARCA_COLORS.secondary,
                            borderColor: ARCA_COLORS.secondary,
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem'
                          }}
                          disabled={product.stock_quantity === 0}
                        >
                          <i className="bi bi-lightning-fill me-1"></i>
                          Buy Now
                        </button>
                        <button
                          onClick={() => addToCart(product)}
                          className="btn btn-sm text-white"
                          style={{
                            backgroundColor: ARCA_COLORS.primary,
                            borderColor: ARCA_COLORS.primary,
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem'
                          }}
                          disabled={product.stock_quantity === 0}
                        >
                          <i className="bi bi-cart-plus me-1"></i>
                          Add to Cart
                        </button>
                        <button
                          onClick={() => navigate(`/store/product/${product.id}`)}
                          className="btn btn-sm btn-outline-secondary"
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem'
                          }}
                          title="View Product Details"
                        >
                          <i className="bi bi-eye me-1"></i>
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default StorePage;